import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import { MAX_RESUME_CHECKPOINT_ENCODED_LENGTH } from '../security/input.js';

function canonicalize(value) {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalize).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalize(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

function signingPayload(checkpoint) {
  const { signatures: _signatures, ...payload } = checkpoint;
  return canonicalize(payload);
}

export function createResumeCheckpoint(game) {
  return {
    schemaVersion: 1,
    gameId: game.id,
    revision: game.revision,
    fen: game.fen,
    turn: game.turn,
    status: game.status,
    clocks: game.clocks,
    lastActionId: game.lastActionId || null,
    lastMoveSignature: game.moves.at(-1)?.signature || null,
    signatures: {}
  };
}

export function signResumeCheckpoint(checkpoint, identity) {
  const signature = nacl.sign.detached(
    naclUtil.decodeUTF8(signingPayload(checkpoint)),
    identity.keyPair.signing.secretKey
  );
  return {
    ...checkpoint,
    signatures: {
      ...checkpoint.signatures,
      [identity.playerId]: naclUtil.encodeBase64(signature)
    }
  };
}

export function verifyResumeSignature(checkpoint, player) {
  const signature = checkpoint?.signatures?.[player?.playerId];
  const publicKey = player?.publicKey?.signingPublicKey;
  if (!signature || !publicKey) {
    return false;
  }
  try {
    return nacl.sign.detached.verify(
      naclUtil.decodeUTF8(signingPayload(checkpoint)),
      naclUtil.decodeBase64(signature),
      naclUtil.decodeBase64(publicKey)
    );
  } catch {
    return false;
  }
}

export function isDualSignedCheckpoint(checkpoint, game) {
  return Boolean(
    game.players.white
    && game.players.black
    && verifyResumeSignature(checkpoint, game.players.white)
    && verifyResumeSignature(checkpoint, game.players.black)
  );
}

export function checkpointMatchesGame(checkpoint, game) {
  return Boolean(
    checkpoint
    && game
    && checkpoint.gameId === game.id
    && checkpoint.revision === game.revision
    && checkpoint.fen === game.fen
    && checkpoint.lastActionId === (game.lastActionId || null)
  );
}

export function encodeResumeCheckpoint(checkpoint) {
  const bytes = new TextEncoder().encode(JSON.stringify(checkpoint));
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

export function decodeResumeCheckpoint(value) {
  const encoded = String(value || '');
  if (!encoded || encoded.length > MAX_RESUME_CHECKPOINT_ENCODED_LENGTH) {
    throw new Error('Invalid resume checkpoint');
  }
  try {
    const normalized = encoded.replaceAll('-', '+').replaceAll('_', '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const binary = atob(padded);
    if (binary.length > MAX_RESUME_CHECKPOINT_ENCODED_LENGTH) {
      throw new Error('Invalid resume checkpoint');
    }
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new Error('Invalid resume checkpoint');
  }
}
